/**
 * Karimadon Store Enhancement Roadmap
 * Shared front-end logic for: Smart Sizing Tool, Made-to-Order, Pre-order Requests,
 * and the Free Shipping Progress Bar.
 *
 * Features that need a real backend (Klaviyo/back-in-stock app, reviews app, live
 * calendar booking) submit through Shopify's native /contact endpoint as a stand-in,
 * so the store owner gets notified by email today and can swap in a proper app later
 * without changing any markup.
 */

(function () {
  'use strict';

  function postToContactForm(fields) {
    var body = new URLSearchParams();
    body.set('form_type', 'contact');
    body.set('utf8', '✓');
    Object.keys(fields).forEach(function (key) {
      body.set(key, fields[key]);
    });

    return fetch('/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  }

  /* ---------------- Smart Sizing Tool ---------------- */
  // Safe to call more than once (e.g. after Quick View injects a fresh copy
  // of this markup via AJAX, well after DOMContentLoaded already fired) —
  // each root is flagged once it's bound so re-running never double-binds.
  function initSmartSizing() {
    document.querySelectorAll('[data-kr-sizing]').forEach(function (root) {
      if (root.dataset.krSizingInit) return;

      var openBtn = root.querySelector('[data-kr-sizing-open]');
      var modal = root.querySelector('.kr-sizing-modal');
      var closeBtn = root.querySelector('[data-kr-sizing-close]');
      var form = root.querySelector('[data-kr-sizing-form]');
      var result = root.querySelector('.kr-sizing-result');
      var applyBtn = root.querySelector('[data-kr-sizing-apply]');
      if (!openBtn || !modal || !form) return;

      root.dataset.krSizingInit = 'true';

      openBtn.addEventListener('click', function () {
        if (typeof modal.showModal === 'function') {
          modal.showModal();
        } else {
          modal.setAttribute('open', 'open');
        }
      });

      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          if (typeof modal.close === 'function') modal.close();
          else modal.removeAttribute('open');
        });
      }

      // CM/Inches toggle — converts whatever the shopper already typed so
      // switching units doesn't lose their numbers, swaps each field's
      // min/max/step, and updates the reference chart table + footnote to
      // match the same unit (single source of truth: the toggle buttons).
      var unitButtons = root.querySelectorAll('[data-kr-sizing-unit-btn]');
      var measurementInputs = root.querySelectorAll('[data-kr-sizing-measurement]');
      var unitSuffixEls = root.querySelectorAll('.kr-sizing-unit-suffix');
      var chartCells = root.querySelectorAll('.kr-sizing-chart__table td[data-in]');
      var chartNoteEl = root.querySelector('.kr-sizing-chart__note-unit');

      unitButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var newUnit = btn.dataset.krSizingUnitBtn;
          var oldUnit = root.dataset.krSizingUnit || 'in';
          if (newUnit === oldUnit) return;

          measurementInputs.forEach(function (input) {
            var raw = parseFloat(input.value);
            if (!isNaN(raw)) {
              var converted = newUnit === 'cm' ? raw * 2.54 : raw / 2.54;
              input.value = Math.round(converted * 10) / 10;
            }
            input.min = input.dataset['min' + (newUnit === 'cm' ? 'Cm' : 'In')];
            input.max = input.dataset['max' + (newUnit === 'cm' ? 'Cm' : 'In')];
            input.step = input.dataset['step' + (newUnit === 'cm' ? 'Cm' : 'In')];
          });

          unitSuffixEls.forEach(function (el) {
            el.textContent = newUnit === 'cm' ? '(cm)' : '(in)';
          });

          chartCells.forEach(function (td) {
            var inVal = parseFloat(td.dataset.in);
            td.textContent = newUnit === 'cm' ? Math.round(inVal * 2.54) + ' cm' : inVal + '"';
          });

          if (chartNoteEl) {
            chartNoteEl.textContent =
              newUnit === 'cm' ? chartNoteEl.dataset.noteCm : chartNoteEl.dataset.noteIn;
          }

          unitButtons.forEach(function (b) {
            b.classList.toggle('is-active', b === btn);
          });

          root.dataset.krSizingUnit = newUnit;
        });
      });

      form.addEventListener('submit', function (event) {
        event.preventDefault();

        var unit = root.dataset.krSizingUnit || 'in';
        var bustRaw = parseFloat(form.querySelector('[name="bust"]').value);
        var waistRaw = parseFloat(form.querySelector('[name="waist"]').value);
        var hipsRaw = parseFloat(form.querySelector('[name="hips"]').value);

        if (!bustRaw || !waistRaw || !hipsRaw) return;

        // recommendSize()/the chart are inches-based — normalize cm input.
        var bustIn = unit === 'cm' ? bustRaw / 2.54 : bustRaw;
        var waistIn = unit === 'cm' ? waistRaw / 2.54 : waistRaw;
        var hipsIn = unit === 'cm' ? hipsRaw / 2.54 : hipsRaw;

        var sizeData = window.krSizeChartData || [];
        var recommendation = recommendSize(bustIn, waistIn, hipsIn, sizeData);

        var sizeEl = result.querySelector('.kr-sizing-result__size');
        var confidenceEl = result.querySelector('.kr-sizing-result__confidence');
        var noteEl = result.querySelector('.kr-sizing-result__note');
        if (sizeEl) sizeEl.textContent = 'Size ' + recommendation.size;
        if (confidenceEl) {
          confidenceEl.textContent =
            'Best fit for your measurements · ' + recommendation.confidence + '% confidence';
        }
        if (noteEl) {
          var isPlusSize = ['XL', '2XL', '3XL'].indexOf(recommendation.size) !== -1;
          noteEl.textContent = isPlusSize
            ? 'Plus sizes (XL-3XL) are available for Special Order.'
            : '';
        }
        result.dataset.krRecommendedSize = recommendation.size;
        result.classList.add('is-visible');
        if (applyBtn) {
          applyBtn.textContent = applyBtn.dataset.krDefaultLabel || applyBtn.textContent;
          applyBtn.classList.remove('is-applied');
        }
      });

      if (applyBtn) {
        applyBtn.dataset.krDefaultLabel = applyBtn.textContent;
        applyBtn.addEventListener('click', function () {
          var size = result.dataset.krRecommendedSize;
          if (!size) return;
          var scope = root.closest('.main-product, .quick-view_grid') || document;
          applyRecommendedSize(size, scope);
          applyBtn.textContent = 'Size Applied ✓';
          applyBtn.classList.add('is-applied');
          if (typeof modal.close === 'function') {
            setTimeout(function () {
              modal.close();
            }, 600);
          }
        });
      }
    });
  }

  // Real Karimadon size chart (inches) — same figures shown in the tool's
  // reference table. A product metafield (custom.size_chart_data) can supply
  // a product-specific chart in the same shape to override this default.
  var KR_DEFAULT_SIZE_CHART = [
    { size: 'XS', bust: 32, waist: 26, hips: 34 },
    { size: 'S', bust: 34, waist: 28, hips: 36 },
    { size: 'M', bust: 36, waist: 30, hips: 38 },
    { size: 'L', bust: 38, waist: 32, hips: 40 },
    { size: 'XL', bust: 40, waist: 34, hips: 42 },
    { size: '2XL', bust: 42, waist: 36, hips: 44 },
    { size: '3XL', bust: 44, waist: 38, hips: 46 },
  ];

  // Matches the shopper's actual Bust/Waist/Hips measurements against the
  // real size chart (nearest match by combined distance) — no height/weight
  // guessing involved.
  function recommendSize(bustIn, waistIn, hipsIn, sizeData) {
    var chart = sizeData && sizeData.length ? sizeData : KR_DEFAULT_SIZE_CHART;
    var sizeOrder = chart.map(function (entry) {
      return entry.size;
    });

    var best = chart[0];
    var bestDistance = Infinity;
    chart.forEach(function (entry) {
      var distance =
        Math.abs(entry.bust - bustIn) + Math.abs(entry.waist - waistIn) + Math.abs(entry.hips - hipsIn);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = entry;
      }
    });

    var sizeIndex = sizeOrder.indexOf(best.size);
    // Fit adjustment for "how the garment actually runs" now comes from the
    // product's own Fit Rating metafield (shown as a separate standing note
    // in the modal), not a shopper-selected preference — this function only
    // matches raw measurements against the chart.

    var confidence = Math.max(70, 98 - Math.round(bestDistance * 3));
    return { size: sizeOrder[sizeIndex], confidence: Math.min(confidence, 98) };
  }

  // The recommendation engine works in abbreviated codes (XS/S/M/L/XL/2XL/
  // 3XL, matching KR_DEFAULT_SIZE_CHART below), but a product's actual size
  // option values are spelled-out words ("Medium", "Extra Large") — an
  // exact-value match against the raw code alone silently finds nothing.
  // Try the code as-is first, then its spelled-out equivalent, both
  // case-insensitively against whatever's actually in the DOM.
  var KR_SIZE_LABEL_MAP = {
    XS: 'Extra Small',
    S: 'Small',
    M: 'Medium',
    L: 'Large',
    XL: 'Extra Large',
    '2XL': '2XL',
    '3XL': '3XL',
  };

  function applyRecommendedSize(size, scope) {
    var candidates = [size];
    if (KR_SIZE_LABEL_MAP[size]) candidates.push(KR_SIZE_LABEL_MAP[size]);

    // The main product page wraps its swatches in <variant-radios>, but
    // Quick View (assets/quickView.js, a separate AJAX-injected copy of
    // this same sizing tool) uses its own <variant-quickview> custom
    // element instead — searching only variant-radios silently finds
    // nothing there. Scope to whichever product context called this (main
    // product vs. quick view) so applying a size in one never touches the
    // other if both happen to be in the DOM at once.
    var root = scope || document;
    var inputs = root.querySelectorAll(
      'variant-radios input[type="radio"], variant-quickview input[type="radio"]'
    );
    var options = root.querySelectorAll('variant-selects select option');
    var applied = false;

    candidates.forEach(function (candidate) {
      if (applied) return;
      var target = candidate.toLowerCase();

      inputs.forEach(function (input) {
        if (applied) return;
        if ((input.value || '').toLowerCase() === target) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          applied = true;
        }
      });
      if (applied) return;

      options.forEach(function (option) {
        if (applied) return;
        if ((option.value || '').toLowerCase() === target) {
          option.selected = true;
          option.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
          applied = true;
        }
      });
    });

    return applied;
  }

  /* ---------------- Made to Order ---------------- */
  function initMadeToOrder() {
    document.querySelectorAll('[data-kr-mto]').forEach(function (root) {
      var toggle = root.querySelector('.kr-mto__toggle');
      var body = root.querySelector('.kr-mto__body');
      var checkbox = root.querySelector('[data-kr-mto-enabled]');
      if (!toggle || !body) return;

      toggle.addEventListener('click', function () {
        var isOpen = body.classList.toggle('is-open');
        if (checkbox) checkbox.checked = isOpen;
        setMtoFieldsDisabled(root, !isOpen);
      });

      setMtoFieldsDisabled(root, true);
    });
  }

  // Disabled inputs are not submitted with the form, so a shopper who never opens
  // the Made-to-Order panel doesn't add empty "Custom Color / Custom Size" line
  // item properties to their cart item.
  function setMtoFieldsDisabled(root, disabled) {
    root.querySelectorAll('.kr-mto__body input, .kr-mto__body select, .kr-mto__body textarea').forEach(function (field) {
      field.disabled = disabled;
    });
  }

  /* ---------------- Restock Notifications ---------------- */
  function initRestockNotify() {
    document.querySelectorAll('[data-kr-restock-form]').forEach(function (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var emailInput = form.querySelector('input[type="email"]');
        var prefInput = form.querySelector('[name="color_size_preference"]');
        var successEl = form.parentElement.querySelector('.kr-restock__success');
        var errorEl = form.parentElement.querySelector('.kr-restock__error');
        var submitBtn = form.querySelector('button[type="submit"]');

        if (!emailInput || !emailInput.value) return;

        if (submitBtn) submitBtn.disabled = true;

        var preference = (prefInput && prefInput.value) || form.dataset.variantTitle || '';

        postToContactForm({
          'contact[email]': emailInput.value,
          'contact[tags]': 'pre-order-request',
          'contact[Product]': form.dataset.productTitle || '',
          'contact[Variant]': form.dataset.variantTitle || '',
          'contact[Color/Size Preference]': preference,
          'contact[body]':
            'Pre-order request for "' +
            (form.dataset.productTitle || '') +
            '" (' +
            (form.dataset.variantTitle || '') +
            ') — preference: ' +
            preference +
            ' — from ' +
            emailInput.value,
        })
          .then(function (response) {
            if (!response.ok) throw new Error('Request failed');
            if (successEl) successEl.classList.add('is-visible');
            if (errorEl) errorEl.classList.remove('is-visible');
            form.reset();
            form.style.display = 'none';
          })
          .catch(function () {
            if (errorEl) errorEl.classList.add('is-visible');
          })
          .finally(function () {
            if (submitBtn) submitBtn.disabled = false;
          });
      });
    });
  }

  /* ---------------- Free Shipping Progress Bar ---------------- */
  function initShippingBar() {
    var bars = document.querySelectorAll('[data-kr-shipping-bar]');
    if (!bars.length) return;

    function render(cart) {
      bars.forEach(function (bar) {
        var threshold = parseFloat(bar.dataset.threshold) * 100; // cents
        var textEl = bar.querySelector('.kr-shipping-bar__text');
        var fillEl = bar.querySelector('.kr-shipping-bar__fill');
        var remaining = threshold - cart.total_price;

        if (remaining <= 0) {
          bar.classList.add('is-complete');
          if (textEl) textEl.textContent = bar.dataset.completeText || "You've unlocked FREE shipping!";
          if (fillEl) fillEl.style.width = '100%';
        } else {
          bar.classList.remove('is-complete');
          var remainingFormatted = formatMoney(remaining, bar.dataset.moneyFormat);
          if (textEl) {
            textEl.textContent = (bar.dataset.progressText || 'Add __AMOUNT__ more for FREE shipping!').replace(
              '__AMOUNT__',
              remainingFormatted
            );
          }
          if (fillEl) fillEl.style.width = Math.min(100, (cart.total_price / threshold) * 100) + '%';
        }
      });
    }

    function formatMoney(cents, format) {
      var amount = (cents / 100).toFixed(2);
      if (format) return format.replace('__AMOUNT__', amount);
      return amount;
    }

    function refresh() {
      fetch('/cart.js')
        .then(function (res) {
          return res.json();
        })
        .then(render)
        .catch(function () {});
    }

    refresh();

    var debounced = debounceLocal(refresh, 400);
    var observer = new MutationObserver(debounced);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    document.body.addEventListener('click', function (event) {
      if (
        event.target.closest('.add-to-cart-btn') ||
        event.target.closest('.quantity_box') ||
        event.target.closest('.quantity__button') ||
        event.target.closest('.remove-product')
      ) {
        setTimeout(refresh, 900);
      }
    });
  }

  function debounceLocal(fn, wait) {
    var timeout;
    return function () {
      clearTimeout(timeout);
      timeout = setTimeout(fn, wait);
    };
  }

  /* ---------------- Made-to-Order cart CTA ---------------- */
  function initMadeToOrderCart() {
    document.querySelectorAll('[data-kr-mto]').forEach(function (root) {
      var openBtn = root.querySelector('[data-kr-mto-open]');
      var modal = root.querySelector('.kr-mto-modal');
      var closeBtn = root.querySelector('[data-kr-mto-close]');
      if (!openBtn || !modal) return;

      openBtn.addEventListener('click', function () {
        if (typeof modal.showModal === 'function') {
          modal.showModal();
        } else {
          modal.setAttribute('open', 'open');
        }
      });

      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          if (typeof modal.close === 'function') modal.close();
          else modal.removeAttribute('open');
        });
      }

      modal.addEventListener('click', function (event) {
        if (event.target === modal) {
          if (typeof modal.close === 'function') modal.close();
          else modal.removeAttribute('open');
        }
      });
    });
  }

  // Client request: "KARIMADON Studio" in the header nav (desktop bar and
  // mobile drawer both) should pop open the Made-to-Order modal instead of
  // navigating to a page — there's no dedicated Studio page/URL to send it
  // to yet. Matches by link text rather than href, since the nav item
  // currently points at a placeholder "/" URL. Reuses the hidden global
  // trigger button (layout/theme.liquid → made-to-order-cta with
  // data-kr-mto-global) so all the open/close/backdrop-click wiring from
  // initMadeToOrderCart() above just works, nothing duplicated here.
  function initStudioNavPopup() {
    var globalTrigger = document.querySelector('[data-kr-mto-global] [data-kr-mto-open]');
    if (!globalTrigger) return;

    document.addEventListener('click', function (event) {
      var link = event.target.closest('a');
      if (!link) return;
      if (!link.closest('header, .menu-drawer, .site-nav, .header-drawer')) return;
      var text = (link.textContent || '').trim().toLowerCase();
      if (text !== 'karimadon studio') return;
      event.preventDefault();
      globalTrigger.click();
    });
  }

  function initCategoryQuickLinks() {
    document.querySelectorAll('.kr-category-links__row-wrapper').forEach(function (wrapper) {
      var row = wrapper.querySelector('[data-kr-category-row]');
      var prevBtn = wrapper.querySelector('[data-kr-category-prev]');
      var nextBtn = wrapper.querySelector('[data-kr-category-next]');
      if (!row) return;

      function scrollByAmount(amount) {
        row.scrollBy({ left: amount, behavior: 'smooth' });
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          scrollByAmount(-240);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          scrollByAmount(240);
        });
      }

      function updateArrowState() {
        var maxScroll = row.scrollWidth - row.clientWidth;
        if (prevBtn) prevBtn.disabled = row.scrollLeft <= 0;
        if (nextBtn) nextBtn.disabled = row.scrollLeft >= maxScroll - 1;
      }

      row.addEventListener('scroll', debounceLocal(updateArrowState, 50));
      window.addEventListener('resize', debounceLocal(updateArrowState, 100));
      updateArrowState();
    });
  }

  // There are two different Instagram-feed apps that have existed on this
  // store — don't confuse them:
  //  1. "Instafeed" by Zegsu Software Solutions (installed 2023, FREE,
  //     widget container #instagenie-feed) — configured as a real section
  //     app-block in templates/index.json, so normal section-order edits
  //     already correctly position it right after "Customer Favorites".
  //     This is the one in use going forward.
  //  2. "Instafeed" by Adevole (installed later, PAID after a ~1200-visit
  //     trial) — a Theme App Extension injecting #stories/#instafeed as
  //     fixed children of <main>, outside the section system entirely, and
  //     gating its own "Powered by" removal behind a paid plan. Decided
  //     against — not referenced here. If it's still installed, it should
  //     be uninstalled from Admin (Apps) to stop it running.
  //
  // This only needs a defensive "hide the badge if one exists" pass, scoped
  // to just this app block's own container — no DOM relocation necessary
  // since section order already handles positioning.
  function initInstafeedBrandingHide() {
    var feed = document.getElementById('instagenie-feed');
    var scope = feed ? (feed.closest('.shopify-app-block') || feed) : null;
    if (!scope) return;

    function hideBadge() {
      var walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
      var node;
      while ((node = walker.nextNode())) {
        if (/powered\s+by/i.test(node.textContent)) {
          var el = node.parentElement;
          if (el && el.style.display !== 'none') {
            el.style.display = 'none';
          }
        }
      }
    }

    hideBadge();
    new MutationObserver(hideBadge).observe(scope, { childList: true, subtree: true });
  }

  /* ---------------- Color Combo Switcher ---------------- */
  // Client-requested: a fixed bottom-right widget letting a shopper pick
  // between the 6 brand-approved color combos — the 4 from the Brand
  // Refresh combo reference sheet (Atelier Ivory / Bridal Rose / Muted
  // Clay / Aged Copper) plus a Black and a White monochrome combo built
  // from the palette's two Tertiary colors, added per client request.
  // Every combo also drives button/badge color now (--color-accent-1/2 +
  // button label color), each paired for contrast against its own button
  // background — not left constant like the first version of this feature.
  // window.krApplyColorCombo is also called from an early inline script in
  // layout/theme.liquid (before this file loads) so a saved choice applies
  // before first paint instead of flashing the default combo.
  var KR_COLOR_COMBOS = {
    ivory: { background: '242, 236, 220', header: '#F2ECDC', text: '76, 55, 54', headerText: '#4C3736', icon: '#CEA1A3', buttonBg: '206, 161, 163', buttonLabel: '76, 55, 54' },
    rose: { background: '206, 161, 163', header: '#CEA1A3', text: '76, 55, 54', headerText: '#4C3736', icon: '#F2ECDC', buttonBg: '76, 55, 54', buttonLabel: '255, 255, 255' },
    clay: { background: '170, 131, 119', header: '#AA8377', text: '76, 55, 54', headerText: '#4C3736', icon: '#F2ECDC', buttonBg: '76, 55, 54', buttonLabel: '255, 255, 255' },
    copper: { background: '206, 124, 82', header: '#CE7C52', text: '76, 55, 54', headerText: '#4C3736', icon: '#4C3736', buttonBg: '76, 55, 54', buttonLabel: '255, 255, 255' },
    // text tracks --color-background-1 (near-black text on the White combo,
    // near-white text on the Black combo) — this is correct for anything
    // that sits directly on the page background (nav bar, Category Quick
    // Links row, body copy), which swaps together with it. The one place
    // this is NOT safe is components with their own fixed, always-light
    // background that never swaps (product card titles/prices) — those are
    // pinned to the brand wine color with a dedicated override instead of
    // relying on this variable; see "Fixed-color exceptions" in
    // karimadon-roadmap.css.
    white: { background: '255, 255, 255', header: '#FFFFFF', text: '25, 25, 25', headerText: '#191919', icon: '#191919', buttonBg: '25, 25, 25', buttonLabel: '255, 255, 255' },
    black: { background: '25, 25, 25', header: '#191919', text: '255, 255, 255', headerText: '#FFFFFF', icon: '#FFFFFF', buttonBg: '255, 255, 255', buttonLabel: '25, 25, 25' },
  };

  window.krApplyColorCombo = function (id) {
    var combo = KR_COLOR_COMBOS[id];
    if (!combo) return;
    var root = document.documentElement.style;
    root.setProperty('--color-background-1', combo.background);
    root.setProperty('--color-text', combo.text);
    root.setProperty('--colorHeader', combo.header);
    root.setProperty('--colorHeaderText', combo.headerText);
    root.setProperty('--colorMenu', combo.header);
    root.setProperty('--colorMenuText', combo.headerText);
    root.setProperty('--colorMenuSocial', combo.headerText);
    root.setProperty('--colorFooter', combo.header);
    root.setProperty('--colorFooterText', combo.headerText);
    root.setProperty('--colorFooterBorder', combo.icon);
    root.setProperty('--kr-logo-icon-color', combo.icon);
    root.setProperty('--kr-logo-text-color', combo.headerText);
    root.setProperty('--color-accent-1', combo.buttonBg);
    root.setProperty('--color-accent-2', combo.buttonBg);
    root.setProperty('--color-base-solid-button-labels', combo.buttonLabel);
    root.setProperty('--kr-combo-button-label', 'rgb(' + combo.buttonLabel + ')');
  };

  function initColorComboSwitcher() {
    var root = document.querySelector('[data-kr-combo-switcher]');
    if (!root) return;
    var toggle = root.querySelector('[data-kr-combo-toggle]');
    var activeSwatch = root.querySelector('[data-kr-combo-active-swatch]');
    var options = root.querySelectorAll('[data-kr-combo-option]');

    function setActive(id) {
      options.forEach(function (btn) {
        btn.classList.toggle('is-active', btn.getAttribute('data-kr-combo-option') === id);
      });
      var activeBtn = root.querySelector('[data-kr-combo-option="' + id + '"]');
      if (activeSwatch && activeBtn) {
        activeSwatch.style.backgroundColor = activeBtn.style.backgroundColor;
      }
    }

    function selectCombo(id) {
      window.krApplyColorCombo(id);
      try {
        localStorage.setItem('kr-color-combo', id);
      } catch (e) {}
      setActive(id);
    }

    toggle.addEventListener('click', function () {
      var isOpen = root.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });

    options.forEach(function (btn) {
      btn.addEventListener('click', function () {
        selectCombo(btn.getAttribute('data-kr-combo-option'));
      });
    });

    document.addEventListener('click', function (event) {
      if (!root.contains(event.target)) {
        root.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
      }
    });

    var saved = 'white';
    try {
      saved = localStorage.getItem('kr-color-combo') || 'white';
    } catch (e) {}
    setActive(saved);
  }

  /* ---------------- Gallery thumbnail -> color swatch sync ---------------- */
  // Client-requested: clicking a gallery thumbnail should select the
  // matching color swatch, not just swap the main image while leaving the
  // "Color" selection out of sync with what's actually on screen. Each
  // thumbnail is tagged server-side (main-product.liquid) with
  // data-kr-thumb-color for whichever variant uses that image. Clicking it
  // finds the matching swatch <label> and clicks it natively, so the
  // theme's own existing variant-selection JS runs exactly as if the
  // shopper had clicked the swatch themselves — no reimplementing that
  // logic here. Delegated on document so it also covers Quick View's
  // AJAX-injected gallery without needing a re-bind call.
  function initThumbColorSync() {
    document.addEventListener('click', function (event) {
      var thumb = event.target.closest('[data-kr-thumb-color]');
      if (!thumb) return;
      var color = thumb.getAttribute('data-kr-thumb-color');
      if (!color) return;
      var scope = thumb.closest('.main-product') || document;
      var labels = scope.querySelectorAll('.variant__button-label');
      for (var i = 0; i < labels.length; i++) {
        if (labels[i].getAttribute('value') === color) {
          if (!labels[i].classList.contains('selected')) {
            labels[i].click();
          }
          break;
        }
      }
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initSmartSizing();
    initThumbColorSync();
    initMadeToOrder();
    initRestockNotify();
    initShippingBar();
    initCategoryQuickLinks();
    initMadeToOrderCart();
    initInstafeedBrandingHide();
    initColorComboSwitcher();
    initStudioNavPopup();
  });

  // Exposed so quickView.js can re-bind the Smart Sizing Tool after it
  // injects Quick View's markup into the DOM via AJAX, long after
  // DOMContentLoaded already fired (initSmartSizing is safe to call again).
  window.krReinitSmartSizing = initSmartSizing;
})();
